import React from 'react';
import { TestExecution } from '../SecretTestRunner';

interface TestProgressIndicatorProps {
  execution: TestExecution;
}

export const TestProgressIndicator: React.FC<TestProgressIndicatorProps> = ({
  execution
}) => {
  const { status, scenarios, startTime, endTime, results } = execution;
  const isRunning = status === 'running';
  const isCompleted = status === 'completed' || status === 'failed';

  // Calculate progress
  const totalTests = scenarios.length;
  const completedTests = results?.details?.length || 0;
  const progressPercentage = totalTests > 0 ? (completedTests / totalTests) * 100 : 0;

  // Calculate duration
  const duration = isRunning && startTime 
    ? Math.floor((Date.now() - startTime.getTime()) / 1000)
    : isCompleted && startTime && endTime
    ? Math.floor((endTime.getTime() - startTime.getTime()) / 1000)
    : 0;

  if (status === 'idle') {
    return (
      <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Test Progress
        </h2>
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No tests running
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        Test Progress
      </h2>

      {/* Overall Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Overall Progress
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {completedTests}/{totalTests} tests
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ${
              isRunning ? 'bg-blue-500' : 
              status === 'completed' ? 'bg-green-500' : 
              status === 'failed' ? 'bg-red-500' : 'bg-gray-400'
            }`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Test Status List */}
      <div className="space-y-3 mb-6">
        {scenarios.map((scenario, index) => {
          const isCurrentTest = isRunning && index === completedTests;
          const isCompletedTest = index < completedTests;
          const testResult = results?.details?.find(d => d.scenario === scenario.name);
          
          return (
            <div 
              key={scenario.id}
              className={`flex items-center space-x-3 p-3 rounded-lg ${
                isCurrentTest ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' :
                isCompletedTest ? 'bg-gray-50 dark:bg-gray-700/50' : 
                'bg-white dark:bg-gray-700'
              }`}
            >
              {/* Status Icon */}
              <div className="flex-shrink-0">
                {isCurrentTest ? (
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                ) : isCompletedTest && testResult ? (
                  testResult.status === 'passed' ? (
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )
                ) : (
                  <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-full" />
                )}
              </div>

              {/* Test Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className={`text-sm font-medium ${
                    isCurrentTest ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'
                  }`}>
                    {scenario.name}
                  </h3>
                  {testResult && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {Math.round(testResult.duration / 1000)}s
                    </span>
                  )}
                </div>
                <p className={`text-xs ${
                  isCurrentTest ? 'text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {isCurrentTest ? 'Running...' : scenario.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Execution Summary */}
      <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Duration:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              {duration > 0 ? `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}` : '--:--'}
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Status:</span>
            <span className={`ml-2 font-medium ${
              isRunning ? 'text-blue-600 dark:text-blue-400' :
              status === 'completed' ? 'text-green-600 dark:text-green-400' :
              status === 'failed' ? 'text-red-600 dark:text-red-400' :
              'text-gray-600 dark:text-gray-400'
            }`}>
              {isRunning ? 'Running' : 
               status === 'completed' ? 'Completed' :
               status === 'failed' ? 'Failed' : 'Idle'}
            </span>
          </div>
        </div>

        {results && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
            <div className="flex items-center justify-between text-sm">
              <div className="flex space-x-4">
                <span className="text-green-600 dark:text-green-400">
                  ✓ {results.passed} passed
                </span>
                {results.failed > 0 && (
                  <span className="text-red-600 dark:text-red-400">
                    ✗ {results.failed} failed
                  </span>
                )}
                {results.skipped > 0 && (
                  <span className="text-yellow-600 dark:text-yellow-400">
                    ⚠ {results.skipped} skipped
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};